import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { nutritionAPI } from "@/lib/nutrition-api";
import type { NutrientCatalogItem, NutrientGroup } from "@/lib/nutrition-api";

export interface NutrientField {
  name: string;
  label: string;
  unit: string;
  slug: string;
}

export interface NutrientCategory {
  title: string;
  group: NutrientGroup;
  fields: NutrientField[];
}

const GROUP_TITLES: Record<NutrientGroup, string> = {
  CARB: "Carbohydrate",
  LIP: "Fat",
  PROT: "Protein",
  VIT: "Vitamin",
  MIN: "Minerals",
  GEN: "General",
};

const GROUP_ORDER: NutrientGroup[] = [
  "CARB",
  "LIP",
  "PROT",
  "VIT",
  "MIN",
  "GEN",
];

// Frontend camelCase form-field convention. The backend has no opinion on
// these — they exist only because react-hook-form keys form state by name.
// Auto-derivation covers ~95% of slugs; the remaining cases below kept
// historical names so existing localStorage drafts and form code don't break.
//
// Adding a new nutrient in the DB does NOT require an entry here — the
// auto-derived camelCase from the slug works for any new row. This map
// only documents the historical drifts.
const LEGACY_FORM_FIELD_BY_SLUG: Record<string, string> = {
  added_sugar: "sugarAdded",
  "total-polyphenols": "totalPolyphenol",
  "total-saturated-fatty-acids": "totalSaturatedFat",
  "total-monounsaturated-fatty-acids": "totalMonounsaturatedFat",
  "total-poly-unsaturated-fatty-acids": "totalPolyunsaturatedFat",
  retinol_vit_a: "retinol",
  folates_b9: "totalFolatesB9",
  vitamin_d: "totalVitaminD",
  vitamin_e: "alphaTocopherolE",
};

// Convert a slug (kebab-case or snake_case) to camelCase form-field name.
// `fiber-total` -> `fiberTotal`, `retinol_vit_a` -> `retinolVitA`.
const slugToFormField = (slug: string): string => {
  return slug
    .toLowerCase()
    .split(/[-_]/)
    .map((part, idx) =>
      idx === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join("");
};

export const formFieldForSlug = (slug: string): string =>
  LEGACY_FORM_FIELD_BY_SLUG[slug] ?? slugToFormField(slug);

interface NutrientCatalog {
  nutrients: NutrientCatalogItem[];
  categories: NutrientCategory[];
  unitBySlug: Record<string, string>;
  labelBySlug: Record<string, string>;
  slugByFormField: Record<string, string>;
  formFieldBySlug: Record<string, string>;
  formFieldNames: string[];
}

const buildDerived = (rows: NutrientCatalogItem[]): NutrientCatalog => {
  const unitBySlug: Record<string, string> = {};
  const labelBySlug: Record<string, string> = {};
  const slugByFormField: Record<string, string> = {};
  const formFieldBySlug: Record<string, string> = {};

  const byGroup = new Map<NutrientGroup, NutrientField[]>();

  for (const row of rows) {
    const formField = formFieldForSlug(row.slug);
    unitBySlug[row.slug] = row.unit;
    labelBySlug[row.slug] = row.name;
    slugByFormField[formField] = row.slug;
    formFieldBySlug[row.slug] = formField;

    const field: NutrientField = {
      name: formField,
      label: row.name,
      unit: row.unit,
      slug: row.slug,
    };

    const fields = byGroup.get(row.group) ?? [];
    fields.push(field);
    byGroup.set(row.group, fields);
  }

  // Sort each group's fields by API `order`.
  for (const [group, fields] of byGroup) {
    const sorted = [...fields].sort((a, b) => {
      const orderA = rows.find((r) => r.slug === a.slug)?.order ?? 0;
      const orderB = rows.find((r) => r.slug === b.slug)?.order ?? 0;
      return orderA - orderB;
    });
    byGroup.set(group, sorted);
  }

  const categories: NutrientCategory[] = GROUP_ORDER.filter((g) =>
    byGroup.has(g)
  ).map((group) => ({
    title: GROUP_TITLES[group],
    group,
    fields: byGroup.get(group) ?? [],
  }));

  const formFieldNames = categories.flatMap((c) => c.fields.map((f) => f.name));

  return {
    nutrients: rows,
    categories,
    unitBySlug,
    labelBySlug,
    slugByFormField,
    formFieldBySlug,
    formFieldNames,
  };
};

/**
 * Fetch the runtime nutrient catalog and derive the structures every food
 * form needs (grouped categories, slug/unit lookups, camelCase mapping).
 *
 * The catalog is effectively static within a session — long staleTime keeps
 * us from refetching across mounts.
 */
export const useNutrientCatalog = () => {
  const query = useQuery({
    queryKey: ["nutrient-catalog"],
    queryFn: () => nutritionAPI.nutrients.list(),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const derived = useMemo<NutrientCatalog | undefined>(
    () => (query.data ? buildDerived(query.data) : undefined),
    [query.data]
  );

  return {
    ...query,
    catalog: derived,
  };
};
